import { useEffect, useMemo, useState } from "react";
import "../styles/giftDetailsPremium.css";
import "../styles/giftAnimations.css";

/* ── Per-occasion theme config ─────────────────────────────────────── */
/*
 * Each theme has a `gradients` array of 4 entries — one per variant.
 * Combined with the 4 CSS variant classes (.gd-var-0 … .gd-var-3),
 * this gives 4 truly distinct visual designs for every occasion.
 *
 * Design system:
 *   V0  Deep Space   — dark bg, white card, glowing border, rich particles
 *   V1  Neon Glow    — vivid saturated bg, dark-tinted card, glowing text
 *   V2  Glass Luxe   — image shows through, frosted glass card, white text
 *   V3  Ink & Dark   — near-black bg, midnight card, accent-pop typography
 */
const THEMES = {
  birthday: {
    gradients: [
      "linear-gradient(150deg,#2a0015 0%,#7c2d12 40%,#f97316 100%)",   // V0 deep night fire
      "linear-gradient(210deg,#ef4444 0%,#f97316 35%,#facc15 100%)",    // V1 vivid fire
      "linear-gradient(80deg,#fdf2f8 0%,#fda4af 50%,#fb923c 100%)",     // V2 pastel sunrise
      "linear-gradient(315deg,#0f0018 0%,#450a0a 55%,#f97316 100%)",    // V3 midnight ember
    ],
    overlay:    "rgba(15,0,40,0.38)",
    badge:      "🎂 Happy Birthday!",
    accent:     "#f97316",
    glow:       "rgba(249,115,22,0.55)",
    cardClass:  "gd-card--birthday",
    particleEmojis: ["🎉","🎊","🎈","🎁","✨","🎀","⭐"],
    animClass:  "anim-birthday",
    ornamentTL: { shape: "balloons",  color1: "#f97316", color2: "#ec4899" },
    ornamentBR: { shape: "confetti",  color1: "#8b5cf6", color2: "#fbbf24" },
  },
  marriageanniversary: {
    gradients: [
      "linear-gradient(150deg,#1e1b4b 0%,#7c3aed 40%,#be185d 100%)",   // V0 purple romance
      "linear-gradient(210deg,#d97706 0%,#f59e0b 40%,#fbbf24 100%)",    // V1 golden shine
      "linear-gradient(80deg,#fdf4ff 0%,#e879f9 50%,#7c3aed 100%)",     // V2 lavender dream
      "linear-gradient(315deg,#0d0020 0%,#4c1d95 60%,#be185d 100%)",    // V3 midnight velvet
    ],
    overlay:    "rgba(10,5,30,0.42)",
    badge:      "💑 Happy Anniversary",
    accent:     "#d97706",
    glow:       "rgba(217,119,6,0.6)",
    cardClass:  "gd-card--anniversary",
    particleEmojis: ["💍","🌹","✨","💛","💫","❤️","🌟"],
    animClass:  "anim-marriage-anniversary",
    ornamentTL: { shape: "rings",     color1: "#d97706", color2: "#fbbf24" },
    ornamentBR: { shape: "rings",     color1: "#be185d", color2: "#d97706" },
  },
  valentine: {
    gradients: [
      "linear-gradient(150deg,#831843 0%,#ec4899 50%,#fda4af 100%)",    // V0 classic pink
      "linear-gradient(210deg,#ef4444 0%,#ec4899 45%,#f43f5e 100%)",    // V1 hot red
      "linear-gradient(80deg,#fff1f2 0%,#fecdd3 50%,#fda4af 100%)",     // V2 soft blush
      "linear-gradient(315deg,#1c0010 0%,#9d174d 60%,#ec4899 100%)",    // V3 dark romance
    ],
    overlay:    "rgba(30,0,15,0.38)",
    badge:      "❤️ Happy Valentine's Day",
    accent:     "#ec4899",
    glow:       "rgba(236,72,153,0.55)",
    cardClass:  "gd-card--valentine",
    particleEmojis: ["❤️","💕","💖","💗","💝","🌹","✨"],
    animClass:  "anim-valentine",
    ornamentTL: { shape: "hearts",    color1: "#ec4899", color2: "#f43f5e" },
    ornamentBR: { shape: "hearts",    color1: "#fda4af", color2: "#ec4899" },
  },
  workanniversary: {
    gradients: [
      "linear-gradient(150deg,#0f172a 0%,#1d4ed8 55%,#0369a1 100%)",   // V0 navy depth
      "linear-gradient(210deg,#fbbf24 0%,#f59e0b 40%,#d97706 100%)",    // V1 gold award
      "linear-gradient(80deg,#e0f2fe 0%,#38bdf8 50%,#1d4ed8 100%)",     // V2 sky blue
      "linear-gradient(315deg,#020617 0%,#1e3a8a 60%,#1d4ed8 100%)",    // V3 deep navy
    ],
    overlay:    "rgba(5,10,30,0.42)",
    badge:      "⭐ Happy Work Anniversary",
    accent:     "#fbbf24",
    glow:       "rgba(251,191,36,0.5)",
    cardClass:  "gd-card--work",
    particleEmojis: ["⭐","🏆","🎯","✨","💼","🌟","🎉"],
    animClass:  "anim-work-anniversary",
    ornamentTL: { shape: "stars",     color1: "#fbbf24", color2: "#f59e0b" },
    ornamentBR: { shape: "stars",     color1: "#60a5fa", color2: "#fbbf24" },
  },
  weddinginvitation: {
    gradients: [
      "linear-gradient(150deg,#3b0764 0%,#be185d 40%,#f9a8d4 100%)",   // V0 purple rose
      "linear-gradient(210deg,#f9a8d4 0%,#f472b6 40%,#e879f9 100%)",    // V1 pink bouquet
      "linear-gradient(80deg,#fdf4ff 0%,#f5d0fe 50%,#f9a8d4 100%)",     // V2 lavender bloom
      "linear-gradient(315deg,#0d001a 0%,#4a044e 60%,#be185d 100%)",    // V3 midnight rose
    ],
    overlay:    "rgba(20,5,15,0.38)",
    badge:      "🌹 You're Invited",
    accent:     "#f9a8d4",
    glow:       "rgba(249,168,212,0.55)",
    cardClass:  "gd-card--wedding",
    particleEmojis: ["🌹","🌸","💐","🦋","✨","🌺","💒"],
    animClass:  "anim-wedding-invitation",
    ornamentTL: { shape: "petals",    color1: "#f9a8d4", color2: "#fce7f3" },
    ornamentBR: { shape: "petals",    color1: "#be185d", color2: "#f9a8d4" },
  },
  "missing-message": {
    gradients: [
      "linear-gradient(150deg,#0c4a6e 0%,#0284c7 50%,#38bdf8 100%)",   // V0 ocean blue
      "linear-gradient(210deg,#06b6d4 0%,#0284c7 40%,#7c3aed 100%)",    // V1 cyan aurora
      "linear-gradient(80deg,#e0f7fa 0%,#80deea 50%,#0284c7 100%)",     // V2 light wave
      "linear-gradient(315deg,#020617 0%,#0c4a6e 60%,#0284c7 100%)",    // V3 deep ocean
    ],
    overlay:    "rgba(5,15,30,0.38)",
    badge:      "💬 Thinking of You",
    accent:     "#38bdf8",
    glow:       "rgba(56,189,248,0.45)",
    cardClass:  "gd-card--message",
    particleEmojis: ["💬","🌊","🌙","⭐","✨","🕊️","💙"],
    animClass:  "anim-default",
    ornamentTL: { shape: "waves",     color1: "#38bdf8", color2: "#0ea5e9" },
    ornamentBR: { shape: "waves",     color1: "#0284c7", color2: "#38bdf8" },
  },
};

const DEFAULT_THEME = {
  gradients: [
    "linear-gradient(150deg,#1e1b4b 0%,#6d28d9 45%,#db2777 100%)",
    "linear-gradient(210deg,#7c3aed 0%,#db2777 50%,#f43f5e 100%)",
    "linear-gradient(80deg,#faf5ff 0%,#c4b5fd 50%,#a78bfa 100%)",
    "linear-gradient(315deg,#020617 0%,#3b0764 60%,#6d28d9 100%)",
  ],
  overlay:    "rgba(10,5,30,0.38)",
  badge:      "🎁 Special Gift",
  accent:     "#a78bfa",
  glow:       "rgba(167,139,250,0.5)",
  cardClass:  "gd-card--default",
  particleEmojis: ["✨","🎁","⭐","💫","🌟","🎊","🪄"],
  animClass:  "anim-default",
};

/* Maps giftkart categoryIds AND journey slugs → theme key */
function resolveTheme(categoryId) {
  const map = {
    // giftkart categoryIds (saved in Firebase paths)
    birthday:            "birthday",
    valentine:           "valentine",
    marriageanniversary: "marriageanniversary",
    workanniversary:     "workanniversary",
    weddinginvitation:   "weddinginvitation",
    // journey slugs (from URL ?occasion=)
    anniversary:         "marriageanniversary",
    invitation:          "weddinginvitation",
    proposal:            "valentine",
    "missing-message":   "missing-message",
    egift:               null,
  };
  const key = map[categoryId];
  if (key === null || key === undefined) return DEFAULT_THEME;
  return THEMES[key] || DEFAULT_THEME;
}

/* Build a stable set of particle descriptors from emoji list */
function buildParticles(emojis) {
  return Array.from({ length: 16 }, (_, i) => ({
    id: i,
    emoji: emojis[i % emojis.length],
    left:     `${(i * 6.25 + 2) % 96}%`,
    delay:    `${((i * 0.55) % 6).toFixed(2)}s`,
    duration: `${(4 + ((i * 0.7) % 5)).toFixed(1)}s`,
    size:     `${1.1 + (i % 3) * 0.35}rem`,
  }));
}

function buildScreenFloatItems(categoryId) {
  const key = String(categoryId || "").toLowerCase();
  const isBirthday = key === "birthday";

  return Array.from({ length: 20 }, (_, i) => ({
    id: i,
    kind: isBirthday ? "balloon" : "heart",
    x: `${45 + ((i * 3.6) % 14)}%`,
    y: `${58 + ((i * 2.2) % 12)}%`,
    delay: `${(i * 0.08).toFixed(2)}s`,
    dur: `${(3.4 + (i % 6) * 0.26).toFixed(2)}s`,
    size: `${2 + (i % 4) * 0.52}rem`,
    drift: `${-150 + ((i * 16) % 300)}px`,
    rotate: `${-15 + ((i * 5) % 30)}deg`,
    hue: `${338 + (i % 4) * 7}`,
    depth: i % 3 === 0 ? "back" : "front",
    blur: i % 3 === 0 ? "1.4px" : "0px",
    scale: i % 3 === 0 ? "0.82" : "1",
  }));
}

/** CSS-only decorative ornament for a corner — no photos */
function Ornament({ shape, color1, color2, position }) {
  return (
    <div
      className={`gd-ornament gd-ornament--${position} gd-ornament--${shape}`}
      aria-hidden
    >
      <div className="gd-orn-blob1" style={{ background: color1 }} />
      <div className="gd-orn-blob2" style={{ background: color2 }} />
      <div className="gd-orn-ring"  style={{ borderColor: color1 }} />
      <div className="gd-orn-ring2" style={{ borderColor: color2 }} />
      <span className="gd-orn-symbol" data-shape={shape} />
    </div>
  );
}

/**
 * Full-screen recipient view — per-occasion themed with 4 distinct designs.
 * variant 0 = Deep Space | 1 = Neon Glow | 2 = Glass Luxe | 3 = Ink & Dark
 */
export default function GiftDetailsLayout({
  gift,
  categoryId,
  variant = 0,
  isMini = false,
  showEnvelope = false,
}) {
  const theme = useMemo(() => resolveTheme(categoryId), [categoryId]);
  const particles = useMemo(
    () => buildParticles(theme.particleEmojis),
    [theme.particleEmojis]
  );
  const shouldUseEnvelope = Boolean(showEnvelope && !isMini);
  const [entryState, setEntryState] = useState(shouldUseEnvelope ? "closed" : "revealed");
  const screenFloatItems = useMemo(
    () => buildScreenFloatItems(categoryId),
    [categoryId]
  );

  useEffect(() => {
    setEntryState(shouldUseEnvelope ? "closed" : "revealed");
  }, [shouldUseEnvelope, gift?.Name, gift?.Greetings, gift?.Tittle, gift?.Message]);

  const v = Math.min(Math.max(Number(variant) || 0, 0), 3);
  const gradient = theme.gradients[v] || theme.gradients[0];
  const senderName = gift.Name || gift.senderName || "Someone special";
  const shortGreeting = gift.Greetings || "A special card is waiting for you";

  const openEnvelope = () => {
    if (entryState !== "closed") return;
    setEntryState("opening");
    window.setTimeout(() => setEntryState("revealed"), 3200);
  };

  return (
    <div
      className={`gd-root ${theme.animClass} gd-var-${v}${isMini ? " gd-root--mini" : ""}`}
      style={{ backgroundImage: `url(${gift.Image1})` }}
    >
      {/* photo blur + dark overlay */}
      <div
        className="gd-overlay"
        style={{
          background: theme.overlay,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      />

      {/* themed gradient wash */}
      <div className="gd-gradient" style={{ background: gradient }} />

      {/* CSS decorative ornaments */}
      {theme.ornamentTL && (
        <Ornament {...theme.ornamentTL} position="tl" />
      )}
      {theme.ornamentBR && (
        <Ornament {...theme.ornamentBR} position="br" />
      )}

      {/* floating particles */}
      <div className="gd-particles" aria-hidden>
        {particles.map((p) => (
          <span
            key={p.id}
            className="gd-particle"
            style={{
              left: p.left,
              animationDelay: p.delay,
              animationDuration: p.duration,
              fontSize: p.size,
            }}
          >
            {p.emoji}
          </span>
        ))}
      </div>

      {shouldUseEnvelope && entryState !== "revealed" ? (
        <button
          type="button"
          className={`gd-envelope-shell gd-envelope-shell--${entryState}`}
          onClick={openEnvelope}
          aria-label="Open e-card envelope"
        >
          <div className="gd-envelope">
            <span className="gd-envelope-spark gd-envelope-spark--left" aria-hidden>
              ✨
            </span>
            <span className="gd-envelope-spark gd-envelope-spark--right" aria-hidden>
              💖
            </span>
            <div className="gd-envelope-flap" />
            <div className="gd-envelope-body" />
            <div className="gd-envelope-front">
              <p className="gd-envelope-from">From {senderName}</p>
              <p className="gd-envelope-greeting">{shortGreeting}</p>
              <div className="gd-envelope-seal" aria-hidden>
                ❤
              </div>
              <p className="gd-envelope-hint">Touch or click to open</p>
            </div>
          </div>
        </button>
      ) : null}

      {shouldUseEnvelope && entryState === "opening" ? (
        <div className="gd-screen-balloons" aria-hidden>
          {screenFloatItems.map((item) => (
            <span
              key={item.id}
              className={`gd-screen-balloons-item gd-screen-balloons-item--${item.kind} gd-screen-balloons-item--${item.depth}`}
              style={{
                "--x": item.x,
                "--y": item.y,
                "--delay": item.delay,
                "--dur": item.dur,
                "--size": item.size,
                "--drift": item.drift,
                "--rot": item.rotate,
                "--hue": item.hue,
                "--blur": item.blur,
                "--scale": item.scale,
              }}
            >
              {item.kind === "heart" ? (
                <span className="gd-heart-shape" />
              ) : (
                <span className="gd-balloon-shape" />
              )}
            </span>
          ))}
        </div>
      ) : null}

      {/* center card */}
      <div
        className={`gd-stage${
          entryState === "closed"
            ? " gd-stage--hidden"
            : entryState === "opening"
              ? " gd-stage--opening"
              : " gd-stage--revealed"
        }`}
      >
        <article
          className={`gd-card ${theme.cardClass}`}
          style={{ "--accent": theme.accent, "--glow": theme.glow }}
        >
          <div className="gd-badge" style={{ color: theme.accent }}>
            {theme.badge}
          </div>

          <div className="gd-address">
            <p className="gd-address-to">
              <span className="gd-label">To</span>
              <strong>{gift.reciever || gift.recipientName || "You"}</strong>
            </p>
          </div>

          <h1 className="gd-title">{gift.Tittle}</h1>

          <div className="gd-divider" style={{ background: theme.accent }} />

          <blockquote className="gd-message">
            <span className="gd-quote-mark">"</span>
            <p>{gift.Message}</p>
            <span className="gd-quote-mark gd-quote-mark--close">"</span>
          </blockquote>

          <footer className="gd-footer">
            {gift.Greetings && (
              <p className="gd-greeting">{gift.Greetings}</p>
            )}
            <p className="gd-from">
              <span className="gd-label">From</span>
              <strong>{gift.Name || gift.senderName || "Someone special"}</strong>
            </p>
          </footer>

          <div className="gd-orb" style={{ background: theme.glow }} />
        </article>
      </div>
    </div>
  );
}
